import React, { useMemo, useState } from "react";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { formatMoney } from "../../../../features/utils/formatMoney";
import Button1 from "../../../../components/form/Button1";
import confirmChangeBankTransferProof from "../../../../controllers/booking/postTicketing/change/confirmChangeBankTransferProof";
import rejectChangeBankTransferProof from "../../../../controllers/booking/postTicketing/change/rejectChangeBankTransferProof";
import { getStaffApiErrorCopy } from "../../../../components/order/StaffApiErrorModal";
import StaffApiErrorModal from "../../../../components/order/StaffApiErrorModal";

function isPendingProof(proof) {
  return String(proof?.status || "").toLowerCase() === "pending";
}

export function getPendingBankTransferProofs(data) {
  const list = Array.isArray(data?.bankTransferProofs)
    ? data.bankTransferProofs
    : [];
  return list
    .filter(isPendingProof)
    .slice()
    .sort((a, b) => {
      const ta = new Date(a?.submittedAt || 0).getTime();
      const tb = new Date(b?.submittedAt || 0).getTime();
      return tb - ta;
    });
}

export function getAllBankTransferProofs(data) {
  const list = Array.isArray(data?.bankTransferProofs)
    ? data.bankTransferProofs
    : [];
  return list.slice().sort((a, b) => {
    const ta = new Date(a?.submittedAt || 0).getTime();
    const tb = new Date(b?.submittedAt || 0).getTime();
    return tb - ta;
  });
}

function money(amount, currency) {
  if (amount == null || amount === "") return "—";
  const formatted = formatMoney(Number(amount) || 0);
  return currency ? `${formatted} ${currency}` : formatted;
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "bg-amber-100 text-amber-900";
  if (s === "confirmed" || s === "approved" || s === "accepted")
    return "bg-emerald-100 text-emerald-800";
  if (s === "rejected" || s === "declined") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-700";
}

/**
 * Ops review — bank transfer proof history with confirm/reject on pending.
 * @param {boolean} [showHistory] when true, list every proof (modal); else pending only (page banner).
 * @param {boolean} [embedded] drop outer amber page chrome for use inside a modal.
 */
export default function ChangeBankTransferProofCard({
  data,
  onUpdated,
  showHistory = false,
  embedded = false,
}) {
  const proofs = useMemo(
    () =>
      showHistory
        ? getAllBankTransferProofs(data)
        : getPendingBankTransferProofs(data),
    [data, showHistory],
  );
  const pendingCount = useMemo(
    () => proofs.filter(isPendingProof).length,
    [proofs],
  );
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [apiError, setApiError] = useState(null);

  if (!proofs.length) return null;

  const changeId = data?._id;
  const due = Number(
    data?.paymentAmount ?? data?.payable ?? data?.pricedAmountNgn ?? 0,
  );

  // Confirm uses the claimed amount on the proof — backend does not allow
  // ops to edit it here.
  async function handleConfirm(proof) {
    const proofId = proof?._id;
    if (!changeId || !proofId) return;
    setBusyId(proofId);
    const payload = { proofId };
    const claimed = Number(proof?.amount);
    if (Number.isFinite(claimed) && claimed > 0) payload.amount = claimed;
    const res = await confirmChangeBankTransferProof(changeId, payload);
    setBusyId(null);
    if (res?.return) {
      enqueueSnackbar("Bank transfer confirmed.", { variant: "success" });
      if (onUpdated) onUpdated(res.data);
      else window.location.reload();
    } else {
      setApiError(getStaffApiErrorCopy(res));
    }
  }

  async function handleReject(proof) {
    const proofId = proof?._id;
    if (!changeId || !proofId) return;
    const text = reason.trim();
    if (!text) {
      enqueueSnackbar("Enter a rejection reason.", { variant: "warning" });
      return;
    }
    setBusyId(proofId);
    const res = await rejectChangeBankTransferProof(changeId, {
      proofId,
      reason: text,
    });
    setBusyId(null);
    if (res?.return) {
      enqueueSnackbar("Bank transfer rejected.", { variant: "success" });
      setRejectingId(null);
      setReason("");
      if (onUpdated) onUpdated(res.data);
      else window.location.reload();
    } else {
      setApiError(getStaffApiErrorCopy(res));
    }
  }

  const shellClass = embedded
    ? "w-full"
    : "w-full border border-amber-200 rounded-xl overflow-hidden bg-[#FFFBEB] mt-4 p-4";

  return (
    <>
      <div className={shellClass}>
        {!embedded ? (
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h2 className="text-base font-bold text-[#92400E]">
                Bank transfer awaiting approval
              </h2>
              <p className="text-xs text-[#B45309] mt-0.5 leading-relaxed">
                The agent uploaded proof. Match it to the bank, then confirm or
                reject. Do not send to the airline until payment is confirmed.
              </p>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded-md bg-amber-100 text-amber-900 shrink-0">
              {pendingCount} pending
            </span>
          </div>
        ) : (
          <p className="text-xs text-[#6D7C94] mb-3 leading-relaxed">
            History of transfer proofs on this change. Confirm or reject
            pending ones after you match the bank. Do not send to the airline
            until confirmed.
          </p>
        )}

        <div className="space-y-3 mt-1">
          {proofs.map((proof) => {
            const id = proof?._id;
            const pending = isPendingProof(proof);
            const currency = proof?.currency || data?.paymentCurrency || "NGN";
            const busy = busyId === id;
            const isRejecting = rejectingId === id;

            return (
              <div
                key={id || proof?.paymentReference}
                className={`rounded-xl border bg-white p-3 ${
                  pending ? "border-amber-200" : "border-[#E8EEF5]"
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusTone(
                      proof?.status,
                    )}`}
                  >
                    {proof?.status || "Unknown"}
                  </span>
                  {proof?.submittedAt ? (
                    <span className="text-[11px] text-[#6D7C94]">
                      {moment(proof.submittedAt).format("DD MMM YYYY, hh:mm A")}
                    </span>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                      Payment reference
                    </div>
                    <div className="font-semibold text-[#0F172A] break-all">
                      {proof?.paymentReference || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                      Amount claimed
                    </div>
                    <div className="font-semibold text-[#0F172A]">
                      {money(proof?.amount, currency)}
                    </div>
                  </div>
                  {proof?.note ? (
                    <div className="sm:col-span-2">
                      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                        Agent note
                      </div>
                      <div className="text-[#0F172A] whitespace-pre-wrap break-words">
                        {proof.note}
                      </div>
                    </div>
                  ) : null}
                  {proof?.reason || proof?.rejectionReason ? (
                    <div className="sm:col-span-2">
                      <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                        Rejection reason
                      </div>
                      <div className="text-red-700">
                        {proof.reason || proof.rejectionReason}
                      </div>
                    </div>
                  ) : null}
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                      Quote due
                    </div>
                    <div className="text-[#0F172A]">{money(due, currency)}</div>
                  </div>
                </div>

                {proof?.url ? (
                  <a
                    href={proof.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center justify-center h-9 px-3 rounded-lg border border-[#CBD5E1] text-xs font-semibold text-primary1 bg-white hover:bg-[#F8FAFC]"
                  >
                    View receipt
                  </a>
                ) : null}

                {pending ? (
                  isRejecting ? (
                    <div className="mt-3 flex flex-col gap-2">
                      <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                        Rejection reason
                      </label>
                      <textarea
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Transfer not found"
                        className="w-full px-3 py-2 rounded-lg border border-[#DBDBDB] text-sm outline-none focus:border-primary1 resize-y"
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setRejectingId(null);
                            setReason("");
                          }}
                          className="h-8 px-3 rounded-lg border border-[#E2E8F0] text-xs font-semibold text-[#475569] bg-white"
                        >
                          Back
                        </button>
                        <Button1
                          loading={busy}
                          className="!bg-red-600 hover:!bg-red-700"
                          onClick={() => handleReject(proof)}
                        >
                          Reject transfer
                        </Button1>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setRejectingId(id);
                          setReason("");
                        }}
                        className="h-8 px-3 rounded-lg border border-red-200 text-xs font-semibold text-red-700 bg-white hover:bg-red-50"
                      >
                        Reject
                      </button>
                      <Button1
                        loading={busy}
                        onClick={() => handleConfirm(proof)}
                      >
                        Confirm transfer
                      </Button1>
                    </div>
                  )
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
    </>
  );
}
